'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { apiClient } from '@/lib/api';
import { getAuthStorage, getValidAccessToken } from '@/lib/authStorage';

const STORAGE_KEY = 'visit_track_last_at';
const COOLDOWN_MS = 30 * 60 * 1000; // 30 минут

function randomHex32() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getCookie(name: string) {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

export default function VisitTracker() {
  const pathname = usePathname() || '/';

  useEffect(() => {
    // Если нет токена — пользователь не авторизован, визит не трекаем
    const token = getValidAccessToken();
    if (!token) return;

    // Комментарий на русском: если "Запомнить меня" выключено — cookie делаем сессионной.
    const storage = getAuthStorage();
    const remember = storage === localStorage;
    document.cookie = remember
      ? 'is_auth=1; Path=/; Max-Age=31536000; SameSite=Lax'
      : 'is_auth=1; Path=/; SameSite=Lax';

    const now = Date.now();
    const lastRaw = localStorage.getItem(STORAGE_KEY);
    const last = lastRaw ? Number(lastRaw) : 0;

    // Троттлинг: не чаще одного раза в 30 минут (чтобы не накручивать визиты API-запросами)
    if (last && Number.isFinite(last) && now - last < COOLDOWN_MS) return;

    // Не блокируем UI — просто дергаем и забываем
    apiClient.trackVisit()
      .then(() => localStorage.setItem(STORAGE_KEY, String(now)))
      .catch(() => {
        // Молча: трекинг не должен ломать сайт
      });
  }, []);

  useEffect(() => {
    // Pageview аналитика должна идти ИЗ БРАУЗЕРА, чтобы PHP видел реальный REMOTE_ADDR (без sudo/Apache правок)
    const path = pathname || '/';
    if (
      path.startsWith('/api') ||
      path.startsWith('/_next') ||
      path.startsWith('/admin') ||
      path.startsWith('/uploads') ||
      path === '/favicon.ico' ||
      path === '/robots.txt' ||
      path === '/sitemap.xml'
    ) {
      return;
    }

    let sid = getCookie('sid');
    if (!/^[a-f0-9]{32}$/i.test(sid)) {
      sid = randomHex32();
      document.cookie = `sid=${sid}; Path=/; Max-Age=31536000; SameSite=Lax`;
    }

    const isAuth = getCookie('is_auth') === '1';

    fetch('/api/analytics/collect/', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        session_id: sid,
        is_auth: isAuth ? 1 : 0,
        path,
        ref: document.referrer || '',
        ua: navigator.userAgent || '',
      }),
      keepalive: true,
    }).catch(() => {
      // Аналитика не должна ломать сайт
    });
  }, [pathname]);

  return null;
}


